import { useEffect, useMemo } from 'react'

import { useListMarketingChannels } from '@/api/react-query'
import { useAppForm } from '@/components/form/form'
import { AdvancedFilterDrawer } from '@/components/table/advanced-filter-drawer'
import { useI18n } from '@/i18n/i18n-react'

// Matches content that sits in no live channel's layout; understood by the ListMarketingContent API.
const CHANNEL_FILTER_NONE = 'NONE'

export interface ContentFilters {
	channels: string[]
}

interface ContentFilterDrawerProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	defaultValues: ContentFilters
	onApply: (filters: ContentFilters) => void
}

export function ContentFilterDrawer({ open, onOpenChange, defaultValues, onApply }: ContentFilterDrawerProps) {
	const { L } = useI18n()
	const channelsQuery = useListMarketingChannels()

	const channelOptions = useMemo(
		() => [
			...(channelsQuery.data ?? []).map(c => ({ value: c.uuid, label: c.name })),
			{ value: CHANNEL_FILTER_NONE, label: L.Marketing.Content.NotInLayout() }
		],
		[channelsQuery.data, L]
	)

	const form = useAppForm({
		defaultValues,
		onSubmit: async ({ value }) => {
			onApply(value)
			onOpenChange(false)
		}
	})

	// The drawer stays mounted between openings, so sync the form with the applied filters each time it
	// opens - otherwise it would keep whatever was typed-but-not-applied in a previous session.
	useEffect(() => {
		if (open) {
			form.reset(defaultValues)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open])

	return (
		<form.AppForm>
			<AdvancedFilterDrawer
				open={open}
				onOpenChange={onOpenChange}
				onClearAll={() => {
					form.reset({ channels: [] })
					onApply({ channels: [] })
					onOpenChange(false)
				}}
				onApplyClick={() => form.handleSubmit()}
			>
				<form.AppField name='channels'>
					{field => (
						<field.ComboboxField
							label={L.Marketing.Content.ChannelFilter()}
							options={channelOptions}
							optionsLoading={channelsQuery.isLoading}
							optional
						/>
					)}
				</form.AppField>
			</AdvancedFilterDrawer>
		</form.AppForm>
	)
}
